import type { ReactNode } from 'react'

import type { TimelineRailRoundMarker } from '../../lib/timelineRailMarkers'
import { useTheme } from '../../theme/ThemeContext'
import {
  TIMELINE_COL_CONTENT,
  TIMELINE_COL_RAIL,
  TIMELINE_COL_TIME,
} from './timelineGrid'
import { TimelineRailAxisLabels } from './TimelineRailAxisLabels'
import { TimelineRailDateColumn } from './TimelineRailDateColumn'
import { useTimelineSpineRegisterContent, useTimelineSpineRegisterDot } from './TimelineSpineContext'

export type TimelineRoundAccent = {
  dot: string
  stem: string
  cardBorder: string
  frameBorder: string
  srStatus: string
}

type Props = {
  roundId: string
  marker: TimelineRailRoundMarker
  angular: boolean
  accent: TimelineRoundAccent
  children: ReactNode
}

/** 单轮网格行：时刻 | 节点 | 内容；竖线由列表级 TimelineListSpine 绘制 */
export function TimelineRoundRail({ roundId, marker, angular, accent, children }: Props) {
  const { theme } = useTheme()
  const setDotRef = useTimelineSpineRegisterDot(roundId)
  const setContentRef = useTimelineSpineRegisterContent(roundId)
  const showAxisHead = marker.showYear || marker.showMonthDay

  return (
    <>
      {showAxisHead ? (
        <>
          <div className={TIMELINE_COL_TIME} aria-hidden />
          <div
            className={[
              TIMELINE_COL_RAIL,
              'relative z-[5] flex w-full min-w-0 justify-center overflow-visible bg-surface py-1.5 sm:py-2',
            ].join(' ')}
          >
            <TimelineRailAxisLabels marker={marker} theme={theme} />
          </div>
          <div className={[TIMELINE_COL_CONTENT, 'min-w-0'].join(' ')} aria-hidden />
        </>
      ) : null}

      <TimelineRailDateColumn marker={marker} angular={angular} />

      <div
        className={[
          TIMELINE_COL_RAIL,
          'relative z-[3] flex justify-center self-start overflow-visible',
        ].join(' ')}
      >
        <span
          ref={setDotRef}
          className={[
            'relative z-[1] block h-3 w-3 shrink-0 border bg-panel shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--line)_85%,transparent)] sm:h-3.5 sm:w-3.5',
            accent.dot,
            angular ? 'rounded-none' : 'rounded-full',
          ].join(' ')}
          aria-hidden
        />
      </div>

      <div
        ref={setContentRef}
        className={[TIMELINE_COL_CONTENT, 'min-h-0 min-w-0 self-start overflow-x-hidden'].join(' ')}
      >
        {children}
      </div>
    </>
  )
}
